import type { useApi } from '@sunucupark/client-react';
import type { MailFolder } from '@sunucupark/shared';
import { routes } from '@sunucupark/shared/lite';
import { contractSchema } from './contracts';

/** The gateway client as the injection hook hands it over (features may not name the class). */
type Api = ReturnType<typeof useApi>;

/** Folders mail lives in; the mailbox's contact, calendar and task folders have screens of their own. */
export function isMailFolder(folder: MailFolder): boolean {
  return folder.view === 'message' || folder.view === 'conversation';
}

/**
 * The mail folders, exactly as every query of `qk.folders` caches them.
 *
 * Several features read that one cache entry, each through a query of its own
 * (features never import each other, ADR 0006) — so they must all write the
 * same thing into it. When one of them cached the whole answer and another
 * only the mail folders, whichever fetched last decided what the other
 * showed, and the address books and calendars turned up in the folder tree.
 */
export async function fetchMailFolders(api: Api, signal?: AbortSignal): Promise<MailFolder[]> {
  const answer = await api.request({
    path: routes.mail.folders,
    // The contracts arrive with the first request, not with the page (ADR 0006).
    schema: contractSchema((contracts) => contracts.FolderListResponseSchema),
    ...(signal ? { signal } : {}),
  });
  return answer.folders.filter(isMailFolder);
}
